import { useEffect, useRef, useState } from 'react';
import { Upload, UploadProps, Button, message } from 'antd';
import { CameraIcon, PlusIcon, TrayArrowUpIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import {
  AssetUploaderClient,
  normalizeAssetUploadError,
  prepareAssetUploadFile,
} from '@admin-panel/providers';
import type { AssetUploadCheckpoint } from '@admin-panel/providers';
import { PACKING_UPLOAD_STALL_MS } from '../../../../providers/src/lib/packingUploadLimits';
import { AssetUsage } from '@admin-panel/types';
import { packingFileSelectionKey } from './packingFileSelection';

type UploadRequestOption = Parameters<NonNullable<UploadProps['customRequest']>>[0];
type BeforeUploadFile = Parameters<NonNullable<UploadProps['beforeUpload']>>[0];
type UploadRequestFile = UploadRequestOption['file'];

interface IAssetUploadProps extends UploadProps {
  usage: AssetUsage;
  usageForFile?: (file: File) => AssetUsage;
  uploadTimeoutMs?: number;
  packingVideoUploads?: boolean;
  packingPackageId?: number;
  enabledDragDrop?: boolean;
  label?: string;
  disabled?: boolean;
  isLoading?: boolean;
  camera?: boolean;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
  // Packing-only controls; generic upload consumers keep their current contract.
  onCancelUploadsReady?: (cancel: (() => void) | undefined) => void;
}

const AssetUploader = (props: IAssetUploadProps) => {
  const {
    usageForFile,
    uploadTimeoutMs,
    packingVideoUploads,
    packingPackageId,
    onCancelUploadsReady,
    ...uploadProps
  } = props;
  const client = new AssetUploaderClient();

  const [loading, setLoading] = useState(false);
  const jobs = useRef(
    new Map<string, { controller: AbortController; request: UploadRequestOption }>(),
  );
  const checkpoints = useRef(new Map<string, AssetUploadCheckpoint>());
  const selected = useRef(new Set<string>());
  const selectionKeys = useRef(new Map<string, string>());
  const requests = useRef(new Map<string, UploadRequestOption>());
  const mounted = useRef(true);
  const fileKey = (file: UploadRequestFile | File) =>
    String(
      (file as File & { uid?: string }).uid ??
        `${(file as File).name}:${(file as File).lastModified}`,
    );
  const cancel = (key: string) => {
    const job = jobs.current.get(key);
    if (!job) return;
    jobs.current.delete(key); // fence callbacks before aborting the request
    job.controller.abort();
    job.request.onError?.(new Error('Upload cancelled. Retry this file or remove it.'));
    props.onUploadEnd?.();
    if (mounted.current) setLoading(jobs.current.size > 0);
  };
  useEffect(() => {
    mounted.current = true;
    onCancelUploadsReady?.(() => {
      [...jobs.current.keys()].forEach(cancel);
      requests.current.clear();
      checkpoints.current.clear();
      selected.current.clear();
      selectionKeys.current.clear();
    });
    return () => {
      mounted.current = false;
      jobs.current.forEach(job => job.controller.abort());
      jobs.current.clear();
      onCancelUploadsReady?.(undefined);
    };
  }, [onCancelUploadsReady]);

  const getBrowserFile = (file: UploadRequestFile | File) => {
    if (file instanceof File) {
      return file;
    }

    throw new Error('Unsupported upload file type.');
  };

  const uploadBrowserFile = async (
    file: File,
    callbacks?: Pick<UploadRequestOption, 'onError' | 'onSuccess' | 'onProgress'>,
    signal?: AbortSignal,
  ) => {
    const { onError, onSuccess } = callbacks ?? {};

    const handleUploadError = (error: unknown) => {
      const uploadError = normalizeAssetUploadError(error);
      onError?.(uploadError);
      message.error(uploadError.message);
      props.onUploadEnd?.();
      setLoading(false);
    };

    try {
      setLoading(true);
      props.onUploadStart?.();
      const preparedFile = await prepareAssetUploadFile(file);
      await client.uploadFile({
        file: preparedFile,
        usage: usageForFile ? usageForFile(preparedFile) : props.usage,
        onSuccess: uploadedFile => {
          onSuccess?.(uploadedFile);
          props.onUploadEnd?.();
          setLoading(false);
        },
        onError: handleUploadError,
        source: 'ASSET',
        packingVideo: packingVideoUploads,
        transfer: {
          packingPackageId,
          ...(signal ? { signal } : {}),
          ...(uploadTimeoutMs ? { timeoutMs: uploadTimeoutMs } : {}),
          onProgress: percent => callbacks?.onProgress?.({ percent }),
        },
      });
    } catch (error: unknown) {
      handleUploadError(error);
    }
  };

  const handleCameraClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        uploadBrowserFile(file);
      }
    };
    input.click();
  };

  const uploadFileToCDN = ({ file, onError, onSuccess, onProgress }: UploadRequestOption) => {
    if (packingVideoUploads) {
      const key = fileKey(file);
      if (jobs.current.has(key)) return { abort: () => cancel(key) };
      cancel(key);
      const controller = new AbortController();
      const request = { file, onError, onSuccess, onProgress } as UploadRequestOption;
      requests.current.set(key, request);
      // Only a deliberate retry can replace an unconfirmed legacy Stream POST.
      // AWS retries always retain their identity and reconcile the exact object.
      const previous = checkpoints.current.get(key);
      if (previous && previous.storageProvider !== 'AWS_S3' && !previous.uploaded)
        checkpoints.current.delete(key);
      jobs.current.set(key, { controller, request });
      setLoading(true);
      props.onUploadStart?.();
      const current = () => mounted.current && jobs.current.get(key)?.controller === controller;
      const end = () => {
        if (!current()) return;
        jobs.current.delete(key);
        setLoading(jobs.current.size > 0);
        props.onUploadEnd?.();
      };
      void (async () => {
        try {
          const preparedFile = await prepareAssetUploadFile(getBrowserFile(file));
          if (!current()) return;
          await client.uploadFile({
            file: preparedFile,
            usage: usageForFile ? usageForFile(preparedFile) : props.usage,
            source: 'ASSET',
            packingVideo: true,
            transfer: {
              packingPackageId,
              ...checkpoints.current.get(key),
              signal: controller.signal,
              timeoutMs: uploadTimeoutMs,
              stallTimeoutMs: PACKING_UPLOAD_STALL_MS,
              onCheckpoint: checkpoint => {
                if (current()) checkpoints.current.set(key, checkpoint);
              },
              onProgress: percent => {
                if (current()) onProgress?.({ percent });
              },
            },
            onSuccess: uploaded => {
              if (current()) {
                // The controlled file list now owns the completed asset. Do not
                // retain another video/File through the retry callback closure.
                requests.current.delete(key);
                checkpoints.current.delete(key);
                onSuccess?.(uploaded);
                end();
              }
            },
            onError: error => {
              if (current()) {
                if (error.retryable === false) requests.current.delete(key);
                onError?.(error);
                message.error(error.message);
                end();
              }
            },
          });
        } catch (error) {
          if (current()) {
            onError?.(normalizeAssetUploadError(error));
            end();
          }
        }
      })();
      return { abort: () => cancel(key) };
    }
    const controller = uploadTimeoutMs ? new AbortController() : undefined;
    void Promise.resolve()
      .then(() =>
        uploadBrowserFile(
          getBrowserFile(file),
          { onError, onSuccess, onProgress },
          controller?.signal,
        ),
      )
      .catch(error => {
        onError?.(normalizeAssetUploadError(error));
        setLoading(false);
      });
    return { abort: () => controller?.abort() };
  };

  const beforeUpload: UploadProps['beforeUpload'] = async (file, fileList) => {
    const key = fileKey(file);
    if (packingVideoUploads) {
      const retained = new Set((props.fileList ?? []).map(item => item.uid));
      selectionKeys.current.forEach((_, id) => {
        if (props.fileList && !retained.has(id) && !fileList.some(item => fileKey(item) === id))
          selectionKeys.current.delete(id);
      });
      const signature = packingFileSelectionKey(file);
      if ([...selectionKeys.current].some(([id, value]) => id !== key && value === signature)) {
        message.info(
          'This file is already selected. Save it, or use Retry upload on the existing selection.',
        );
        return Upload.LIST_IGNORE;
      }
      // Reserve before awaiting preparation so a batch cannot start duplicate transfers.
      selectionKeys.current.set(key, signature);
    }
    if (packingVideoUploads && props.maxCount) {
      // rc-upload invokes the whole batch before onChange; reserve slots synchronously.
      const retained = new Set((props.fileList ?? []).map(item => item.uid));
      selected.current.forEach(id => {
        if (!retained.has(id) && !fileList.some(item => fileKey(item) === id))
          selected.current.delete(id);
      });
      if (
        !selected.current.has(key) &&
        new Set([...retained, ...selected.current]).size >= props.maxCount
      ) {
        message.error(`Only ${props.maxCount} more packing media files can be selected.`);
        selectionKeys.current.delete(key);
        return Upload.LIST_IGNORE;
      }
      selected.current.add(key);
    }
    try {
      // Optional per-file routing keeps existing uploaders unchanged.
      usageForFile?.(file as File);
      const preparedFile = await prepareAssetUploadFile(file as File);

      if (!props.beforeUpload) {
        return preparedFile;
      }

      const nextResult = await props.beforeUpload(preparedFile as BeforeUploadFile, fileList);

      if (nextResult === false || nextResult === Upload.LIST_IGNORE) {
        selected.current.delete(key);
        selectionKeys.current.delete(key);
        // A declined packing selection must not become an unsaved list entry.
        // Keep Ant Design's ordinary beforeUpload=false behavior for other consumers.
        return packingVideoUploads ? Upload.LIST_IGNORE : nextResult;
      }

      return nextResult ?? preparedFile;
    } catch (error: unknown) {
      selected.current.delete(key);
      selectionKeys.current.delete(key);
      message.error(normalizeAssetUploadError(error).message);
      return Upload.LIST_IGNORE;
    }
  };

  const getUploadButton = () => {
    if (props.disabled) return <Button disabled>{props.label ?? 'Upload'}</Button>;

    if (props.enabledDragDrop) {
      return (
        <p className="ant-upload-drag-icon">
          <TrayArrowUpIcon />
          <div style={{ fontSize: 10 }}>Drag and Drop file here</div>
        </p>
      );
    } else if (props.camera) {
      return <CameraIcon onClick={handleCameraClick} />;
    } else {
      if (props.listType === 'text') {
        return (
          <Button icon={<UploadSimpleIcon />} disabled={props.disabled}>
            {props.label ? props.label : 'Upload'}
          </Button>
        );
      } else {
        return (
          <button style={{ padding: 24, border: 'none' }} type="button" disabled={props.disabled}>
            <PlusIcon />
            <div style={{ marginTop: 8 }}>Upload</div>
          </button>
        );
      }
    }
  };

  const Uploader = props.enabledDragDrop ? Upload.Dragger : Upload;

  return (
    <Uploader
      {...uploadProps}
      disabled={(!packingVideoUploads && loading) || props.disabled || props.isLoading}
      name="uploadList"
      listType={props.listType}
      customRequest={uploadFileToCDN}
      beforeUpload={beforeUpload}
      onRemove={file => {
        if (packingVideoUploads) {
          cancel(file.uid);
          selected.current.delete(file.uid);
          selectionKeys.current.delete(file.uid);
          checkpoints.current.delete(file.uid);
          requests.current.delete(file.uid);
        }
        return props.onRemove?.(file) ?? true;
      }}
      itemRender={
        props.itemRender ??
        (packingVideoUploads
          ? (origin, file) => (
              <div>
                {origin}
                {file.status === 'uploading' && (
                  <Button size="small" onClick={() => cancel(file.uid)}>
                    Cancel upload
                  </Button>
                )}
                {file.status === 'error' && requests.current.has(file.uid) && (
                  <Button
                    size="small"
                    disabled={props.disabled || jobs.current.has(file.uid)}
                    onClick={() => uploadFileToCDN(requests.current.get(file.uid)!)}
                  >
                    Retry upload
                  </Button>
                )}
              </div>
            )
          : undefined)
      }
      multiple={props.multiple}
    >
      {props.children ?? getUploadButton()}
    </Uploader>
  );
};

export default AssetUploader;
